import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import BarcodeScannerComponent from 'react-qr-barcode-scanner';
import {
  useCreatePurchaseReturnMutation,
  useGetPurchaseReturnQuery,
  useLazyGetSuppliersQuery,
} from '../../../store/api/purchaseApi';
import { useLazyGetProductsQuery } from '../../../store/api/productsApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { formatItemDisplayName } from '../../sales/utils/formatItemDisplayName';

function PurchaseReturnForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isView = !!id;
  const [searchParams] = useSearchParams();
  const supplierIdFromUrl = searchParams.get('supplierId') || '';
  const [createPurchaseReturn] = useCreatePurchaseReturnMutation();
  const [triggerSupplierSearch, { data: supplierSearchData, isFetching: searchingSuppliers }] = useLazyGetSuppliersQuery();
  const [supplierSearchOptions, setSupplierSearchOptions] = useState([]);
  const [triggerProductSearch, { data: productSearchData, isFetching: searchingProducts }] = useLazyGetProductsQuery();
  const [productSearch, setProductSearch] = useState('');
  const [scanOpen, setScanOpen] = useState(false);
  const [scanError, setScanError] = useState('');
  const [pendingBarcode, setPendingBarcode] = useState('');
  const { data: company } = useGetCurrentCompanyQuery();
  const itemNameFormat = company?.printItemNameFormat || 'name_sku';
  const currency = company?.currency || 'USD';
  const { data: existingReturn, isLoading: loadingReturn } = useGetPurchaseReturnQuery(id, {
    skip: !isView,
  });

  const [formData, setFormData] = useState({
    supplierId: '',
    returnDate: new Date().toISOString().split('T')[0],
    notes: '',
    discountTotal: 0,
    roundOff: 0,
    purchaseOrderId: '',
  });

  useEffect(() => {
    if (supplierSearchData?.data) {
      setSupplierSearchOptions(supplierSearchData.data);
    } else if (Array.isArray(supplierSearchData)) {
      setSupplierSearchOptions(supplierSearchData);
    }
  }, [supplierSearchData]);

  useEffect(() => {
    if (existingReturn?.supplier && !supplierSearchOptions.find(s => s.id === existingReturn.supplier.id)) {
      setSupplierSearchOptions(prev => [existingReturn.supplier, ...prev]);
    }
  }, [existingReturn, supplierSearchOptions]);

  const selectedSupplier = supplierSearchOptions.find((s) => s.id === formData.supplierId) || null;

  useEffect(() => {
    if (!isView && supplierIdFromUrl) {
      setFormData((prev) => ({ ...prev, supplierId: supplierIdFromUrl }));
    }
  }, [isView, supplierIdFromUrl]);

  const [items, setItems] = useState([]);

  const handleAddItem = () => {
    setItems([...items, { productId: '', productName: '', productSku: '', productBrand: '', quantity: 1, unitPrice: 0 }]);
  };

  const handleAddProductRow = (product) => {
    if (!product) return;
    setItems((prev) => {
      const existingIndex = prev.findIndex((it) => String(it.productId) === String(product.id));
      if (existingIndex !== -1) {
        const newItems = [...prev];
        newItems[existingIndex].quantity = (parseFloat(newItems[existingIndex].quantity) || 0) + 1;
        return newItems;
      }

      // Fill first empty row if exists
      const emptyIndex = prev.findIndex((it) => !it.productId);
      if (emptyIndex !== -1) {
        const newItems = [...prev];
        newItems[emptyIndex] = {
          productId: product.id,
          productName: product.name,
          productSku: product.sku,
          productBrand: product.brand,
          quantity: 1,
          unitPrice: product.costPrice || 0,
        };
        return newItems;
      }

      return [
        ...prev,
        {
          productId: product.id,
          productName: product.name,
          productSku: product.sku,
          productBrand: product.brand,
          quantity: 1,
          unitPrice: product.costPrice || 0,
        },
      ];
    });
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const vatRate = toNum(company?.vatRate || 0) / 100;
  const vatIncluded = company?.purchaseVatIncluded !== false;

  const subtotal = items.reduce(
    (sum, item) => sum + parseFloat(item.quantity || 0) * parseFloat(item.unitPrice || 0),
    0
  );
  const discountTotal = Math.max(0, Math.min(toNum(formData.discountTotal), subtotal));
  const taxable = Math.max(0, subtotal - discountTotal);
  const vatTotal =
    vatRate > 0
      ? vatIncluded
        ? taxable - taxable / (1 + vatRate)
        : taxable * vatRate
      : 0;
  const grandTotal = vatRate > 0 ? (vatIncluded ? taxable : taxable + vatTotal) : taxable;
  const roundOff = toNum(formData.roundOff);
  const totalAmount = grandTotal + roundOff;

  useEffect(() => {
    if (existingReturn && isView) {
      setFormData((prev) => ({
        ...prev,
        supplierId: existingReturn.supplierId || '',
        returnDate: existingReturn.returnDate
          ? existingReturn.returnDate.substring(0, 10)
          : prev.returnDate,
        notes: existingReturn.notes || '',
        discountTotal: existingReturn.discountTotal ?? 0,
        roundOff: existingReturn.roundOff ?? 0,
        purchaseOrderId: existingReturn.purchaseOrderId || '',
      }));
      setItems(
        (existingReturn.items || []).map((it) => ({
          productId: it.productId,
          productName: it.product?.name,
          productSku: it.product?.sku,
          productBrand: it.product?.brand,
          quantity: it.quantity,
          unitPrice: it.unitPrice,
          isDamaged: it.isDamaged || false,
          damageReason: it.damageReason || '',
        }))
      );
    }
  }, [existingReturn, isView]);

  useEffect(() => {
    if (!scanOpen) {
      setScanError('');
      setPendingBarcode('');
    }
  }, [scanOpen]);

  useEffect(() => {
    if (!pendingBarcode) return;
    const list = productSearchData?.products || [];
    if (!Array.isArray(list) || list.length === 0) return;
    const code = String(pendingBarcode).trim();
    if (!code) return;
    const match = list.find(
      (p) =>
        String(p?.barcode || '').trim() === code ||
        String(p?.sku || '').trim() === code
    );
    if (match) {
      handleAddProductRow(match);
      setProductSearch('');
      setPendingBarcode('');
      setScanOpen(false);
    }
  }, [pendingBarcode, productSearchData]);

  useEffect(() => {
    const val = String(productSearch).trim();
    if (!val || val.length < 3) return;
    if (searchingProducts) return;
    const list = productSearchData?.products || [];
    if (list.length === 0) return;

    const match = list.find((p) => 
      String(p?.barcode || '').trim() === val || 
      String(p?.sku || '').trim() === val
    );

    if (match) {
      handleAddProductRow(match);
      setProductSearch('');
    }
  }, [productSearchData, productSearch, searchingProducts]);

  if (isView && loadingReturn) return <Typography>Loading...</Typography>;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isView) {
      navigate('/purchases/returns');
      return;
    }
    if (!formData.supplierId) {
      alert('Supplier is required');
      return;
    }
    if (!formData.returnDate) {
      alert('Return Date is required');
      return;
    }
    if (!items.length) {
      alert('Please add at least one item');
      return;
    }
    if (items.some((i) => !i.productId)) {
      alert('Please select a product for all items');
      return;
    }

    try {
      await createPurchaseReturn({
        ...formData,
        discountTotal: toNum(formData.discountTotal),
        roundOff: toNum(formData.roundOff),
        items: items.map((item) => ({
          productId: item.productId,
          quantity: parseFloat(item.quantity),
          unitPrice: parseFloat(item.unitPrice),
          isDamaged: item.isDamaged || false,
          damageReason: item.damageReason || '',
        })),
      }).unwrap();
      navigate('/purchases/returns');
    } catch (error) {
      alert(error?.data?.message || 'Error creating purchase return');
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        {isView ? 'Purchase Return Details' : 'New Purchase Return'}
      </Typography>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                fullWidth
                options={supplierSearchOptions}
                loading={searchingSuppliers}
                getOptionLabel={(opt) => opt?.name || ''}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                value={selectedSupplier}
                onChange={(_, newValue) => {
                  setFormData({ ...formData, supplierId: newValue?.id || '' });
                }}
                onInputChange={(_, newInputValue) => {
                  if (newInputValue && newInputValue.length >= 2) {
                    triggerSupplierSearch({ search: newInputValue, limit: 20 });
                  } else if (!newInputValue) {
                    triggerSupplierSearch({ limit: 20 });
                  }
                }}
                onOpen={() => {
                  if (supplierSearchOptions.length === 0) triggerSupplierSearch({ limit: 20 });
                }}
                disabled={isView}
                renderInput={(params) => <TextField {...params} label="Supplier" required={!formData.supplierId} />}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="date"
                label="Return Date"
                name="returnDate"
                value={formData.returnDate}
                onChange={(e) => setFormData({ ...formData, returnDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Return Items
                </Typography>
                {!isView && (
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', minWidth: { xs: '100%', sm: 420 }, maxWidth: 600 }}>
                    <Autocomplete
                      fullWidth
                      size="medium"
                      autoHighlight
                      freeSolo
                      options={searchingProducts ? [] : (productSearchData?.products || [])}
                      loading={searchingProducts}
                      filterOptions={(x) => x}
                      value={null}
                      inputValue={productSearch}
                      getOptionLabel={(option) => {
                        return formatItemDisplayName(
                          { name: option.name, sku: option.sku || option.barcode, brand: option.brand },
                          itemNameFormat === 'name' ? 'name_sku' : itemNameFormat
                        );
                      }}
                      onInputChange={(_, value) => {
                        setProductSearch(value);
                        if (value && value.trim().length >= 1) {
                          triggerProductSearch({ search: value.trim(), limit: 20 });
                        }
                      }}
                      onChange={(_, value) => {
                        if (value && typeof value === 'object') {
                          handleAddProductRow(value);
                          setProductSearch('');
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          id="product-search-input"
                          label="Scan or search item (name / barcode / brand)"
                          placeholder="Type or paste name / barcode / brand"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const list = searchingProducts ? [] : (productSearchData?.products || []);
                              if (list.length > 0) {
                                const typed = String(productSearch).trim();
                                const firstItem = list[0];
                                if (typed) {
                                  const isExactMatch = 
                                    String(firstItem.barcode || '').trim() === typed || 
                                    String(firstItem.sku || '').trim() === typed ||
                                    firstItem.name.toLowerCase().includes(typed.toLowerCase()) ||
                                    (firstItem.brand && firstItem.brand.toLowerCase().includes(typed.toLowerCase()));
                                  if (!isExactMatch) return;
                                }
                                handleAddProductRow(firstItem);
                                setProductSearch('');
                              }
                            }
                          }}
                          sx={{
                            '& .MuiInputBase-root': {
                              height: 56,
                              borderRadius: 2,
                            },
                          }}
                        />
                      )}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<QrCodeScannerIcon />}
                      sx={{ whiteSpace: 'nowrap', height: 56 }}
                      onClick={() => setScanOpen(true)}
                    >
                      Scan
                    </Button>
                    <Button 
                      startIcon={<AddIcon />} 
                      onClick={handleAddItem} 
                      variant="outlined"
                      sx={{ whiteSpace: 'nowrap', height: 56 }}
                    >
                      Empty Row
                    </Button>
                  </Box>
                )}
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600, width: 56, textAlign: 'center' }}>S.No</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Quantity</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{`Unit Price (${currency})`}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{`Total (${currency})`}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Damaged</TableCell>
                      {!isView && <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, index) => {
                      const total = parseFloat(item.quantity || 0) * parseFloat(item.unitPrice || 0);
                      return (
                        <TableRow key={index} hover>
                          <TableCell sx={{ textAlign: 'center' }}>{index + 1}</TableCell>
                          <TableCell>
                            {!isView && !item.productId ? (
                              <Autocomplete
                                fullWidth
                                size="small"
                                options={productSearchData?.products || []}
                                loading={searchingProducts}
                                value={null}
                                getOptionLabel={(option) => {
                                  return formatItemDisplayName(
                                    { name: option.name, sku: option.sku || option.barcode, brand: option.brand },
                                    itemNameFormat === 'name' ? 'name_sku' : itemNameFormat
                                  );
                                }}
                                onInputChange={(_, value) => {
                                  if (value && value.trim().length >= 1) {
                                    triggerProductSearch({ search: value.trim(), limit: 20 });
                                  }
                                }}
                                onChange={(_, value) => {
                                  if (value) {
                                    handleItemChange(index, 'productId', value.id);
                                    handleItemChange(index, 'productName', value.name);
                                    handleItemChange(index, 'productSku', value.sku);
                                    handleItemChange(index, 'productBrand', value.brand);
                                    handleItemChange(index, 'unitPrice', value.costPrice || 0);
                                  }
                                }}
                                renderInput={(params) => <TextField {...params} label="Search product..." />}
                                sx={{ minWidth: 200 }}
                              />
                            ) : (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {formatItemDisplayName(
                                    { name: item.productName || 'Unnamed Product', sku: item.productSku, brand: item.productBrand },
                                    itemNameFormat
                                  )}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                              sx={{ width: 100 }}
                              inputProps={{ min: 0.01, step: 0.01 }}
                              disabled={isView}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={item.unitPrice}
                              onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                              sx={{ width: 120 }}
                              inputProps={{ min: 0, step: 0.01 }}
                              disabled={isView}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{total.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Checkbox
                                size="small"
                                checked={!!item.isDamaged}
                                onChange={(e) => handleItemChange(index, 'isDamaged', e.target.checked)}
                                disabled={isView}
                                sx={{ p: 0 }}
                              />
                              {item.isDamaged && (
                                <TextField
                                  size="small"
                                  placeholder="Reason"
                                  value={item.damageReason || ''}
                                  onChange={(e) => handleItemChange(index, 'damageReason', e.target.value)}
                                  disabled={isView}
                                  sx={{ minWidth: 100 }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          {!isView && (
                            <TableCell sx={{ textAlign: 'center' }}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveItem(index)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                    {items.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={isView ? 5 : 6} align="center" sx={{ py: 3 }}>
                          <Typography color="text.secondary">No items added yet</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mt: 1 }}>
                Return Summary (Auto Calculated)
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label={`Subtotal (${currency})`}
                value={subtotal.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label={`Discount Total (${currency})`}
                name="discountTotal"
                value={formData.discountTotal}
                onChange={(e) => setFormData({ ...formData, discountTotal: e.target.value })}
                inputProps={{ min: 0, step: 0.01 }}
                disabled={isView}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label={`VAT Total (${currency})`}
                value={vatTotal.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label={`Grand Total (${currency})`}
                value={grandTotal.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label={`Round Off (${currency})`}
                name="roundOff"
                value={formData.roundOff}
                onChange={(e) => setFormData({ ...formData, roundOff: e.target.value })}
                inputProps={{ step: 0.01 }}
                disabled={isView}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label={`Total (${currency})`}
                value={totalAmount.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                InputProps={{ readOnly: true }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  type={isView ? 'button' : 'submit'}
                  variant="contained"
                  disabled={!isView && items.length === 0}
                  onClick={isView ? () => navigate('/purchases/returns') : undefined}
                >
                  {isView ? 'Back to Returns' : 'Create Return'}
                </Button>
                {!isView && (
                  <Button variant="outlined" onClick={() => navigate('/purchases/returns')}>
                    Cancel
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Dialog open={scanOpen} onClose={() => setScanOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Scan Item Barcode</DialogTitle>
        <DialogContent>
          {scanError ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {scanError}
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              Point the camera at the barcode. The item will be added to the return when matched.
            </Alert>
          )}
          {scanOpen && (
            <BarcodeScannerComponent
              width="100%"
              height={360}
              delay={200}
              constraints={{ facingMode: 'environment' }}
              onUpdate={(err, result) => {
                if (err) {
                  if (err?.name === 'NotAllowedError') {
                    setScanError('Camera permission denied. Please allow camera access and try again.');
                  } else if (err?.name !== 'NotFoundException') {
                    setScanError('Unable to read barcode. Try better lighting or move closer.');
                  }
                  return;
                }
                const text = result?.text ? String(result.text).trim() : '';
                if (!text) return;
                setProductSearch(text);
                setPendingBarcode(text);
                triggerProductSearch({ search: text, limit: 20 });
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setScanOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PurchaseReturnForm;

